/* eslint-disable camelcase */
import produce from "immer"
import {
    ASC,
    LOW_DEMAND_MODE,

    IEC_61508_2010,
    IEC_61508_1998,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,

    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo2D,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
    ARCHITECTURE_2oo4,
    ARCHITECTURE_3oo3,
} from "const"
import {
    SilSafetyFunction,
    SilSubsystem,
} from "server/models"
import {
    InferValueTypes,
    SilCalculationMethod,
    SilArchitecture,
} from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

type ArchitectureArrayType = { [key in SilCalculationMethod]: SilArchitecture[] }
export const silArchitectureArray: ArchitectureArrayType = {
    [NO_DANGEROUS_FAILURE]: [],
    [NO_SAFE_FAILURE]: [],
    [IEC_61508_2010]: [
        ARCHITECTURE_1oo1,
        ARCHITECTURE_1oo2,
        ARCHITECTURE_1oo2D,
        ARCHITECTURE_1oo3,
        ARCHITECTURE_2oo2,
        ARCHITECTURE_2oo3,
    ],
    [IEC_61508_1998]: [
        ARCHITECTURE_1oo1,
        ARCHITECTURE_1oo2,
        ARCHITECTURE_1oo2D,
        ARCHITECTURE_2oo2,
        ARCHITECTURE_2oo3,
    ],
    [ISA_TR84_2002]: [
        ARCHITECTURE_1oo1,
        ARCHITECTURE_1oo2,
        ARCHITECTURE_1oo3,
        ARCHITECTURE_2oo2,
        ARCHITECTURE_2oo3,
        ARCHITECTURE_2oo4,
    ],
    [ISA_TR84_2022]: [
        ARCHITECTURE_1oo1,
        ARCHITECTURE_1oo2,
        ARCHITECTURE_1oo3,
        ARCHITECTURE_2oo2,
        ARCHITECTURE_2oo3,
        ARCHITECTURE_2oo4,
        ARCHITECTURE_3oo3,
    ],
    [VDI_VDE_2180_2019]: [
        ARCHITECTURE_1oo1,
        ARCHITECTURE_1oo2,
        ARCHITECTURE_1oo3,
        ARCHITECTURE_2oo2,
        ARCHITECTURE_2oo3,
    ],
}

export const silArchitectureBlockLength = {
    [ARCHITECTURE_1oo1]: 1,
    [ARCHITECTURE_1oo2]: 2,
    [ARCHITECTURE_1oo2D]: 2,
    [ARCHITECTURE_2oo2]: 2,
    [ARCHITECTURE_2oo3]: 3,
    [ARCHITECTURE_1oo3]: 3,
    [ARCHITECTURE_2oo4]: 4,
    [ARCHITECTURE_3oo3]: 3,
}


export const initSilSafetyFunction: SilSafetyFunction = {
    id: null,
    projectId: null,
    name: "",
    description: "",
    calculationMethodDangerous: null,
    calculationMethodSafe: null,
    mode: LOW_DEMAND_MODE,
    architecturalConstraints: null,
    targetSil: 1,
    targetPfd: null,
    targetStl: 1,
    targetStr: null,
    copy: 0,
    calculated: false,
}

export const initSilSubsystem: SilSubsystem = {
    id: null,
    safetyFunctionId: null,
    name: "",
    architecture: null,
    architecturalConstraints: null,

    iec615082010t1: "",
    iec615082010mttr: "",
    iec615082010mrt: "",
    iec615082010beta: "",
    iec615082010betaD: "",
    iec615082010k: "",

    iec615081998t1: "",
    iec615081998mttr: "",
    iec615081998beta: "",

    tr842002t1: "",
    tr842002mttr: "",
    tr842002beta: "",
    tr842002safeFailure: "",
    tr842002dangerousFailure: "",

    vdivde21802019ptc0A: "",
    vdivde21802019ptc1A: "",
    vdivde21802019ptc0B: "",
    vdivde21802019ptc1B: "",
    vdivde21802019ptc0C: "",
    vdivde21802019ptc1C: "",
    vdivde21802019beta: "",
    vdivde21802019t0: "",
    vdivde21802019t1: "",
    vdivde21802019t2: "",

    isatr842022ti: "",
    isatr842022di: "",
    isatr842022mttr: "",
    isatr842022beta: "",
    isatr842022safeState: null,

    order: 0,
    blocks: [],
}


const initSilSafetyFunctionState: types.SilSafetyFunctionState = {
    baseRoute: null,

    safetyFunctionList: {},
    safetyFunctionIds: [],
    safetyFunctionView: initSilSafetyFunction,
    safetyFunctionEdit: initSilSafetyFunction,

    subsystemList: [],

    sortColumn: "name",
    sortOrder: ASC,
    filters: {
        search: null,
    },

    reloadList: true,
    reloadView: true,
    reloadEdit: true,
}

const silSafetyFunctionReducer = produce((
    draft: types.SilSafetyFunctionState = initSilSafetyFunctionState,
    action: ActionTypes = null,
): types.SilSafetyFunctionState => {
    switch (action.type) {
        case types.SIL_SAFETY_FUNCTION_BASE_ROUTE:
            draft.baseRoute = action.baseRoute
            return draft

        case types.SIL_SAFETY_FUNCTION_LIST:
            draft.safetyFunctionList = action.safetyFunctionList
            return draft

        case types.SIL_SAFETY_FUNCTION_IDS:
            draft.safetyFunctionIds = action.safetyFunctionIds
            return draft

        case types.SIL_SAFETY_FUNCTION_VIEW:
            draft.safetyFunctionView = action.safetyFunction
            return draft

        case types.SIL_SAFETY_FUNCTION_EDIT:
            draft.safetyFunctionEdit = action.safetyFunction
            return draft

        case types.SIL_SAFETY_FUNCTION_SUBSYSTEM_LIST:
            draft.subsystemList = action.subsystemList
            return draft

        case types.SIL_SAFETY_FUNCTION_SORT:
            draft.sortColumn = action.sortColumn
            draft.sortOrder = action.sortOrder
            return draft

        case types.SIL_SAFETY_FUNCTION_FILTERS:
            draft.filters = action.filters
            return draft

        case types.SIL_SAFETY_FUNCTION_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        case types.SIL_SAFETY_FUNCTION_RELOAD_VIEW:
            draft.reloadView = action.value
            return draft

        case types.SIL_SAFETY_FUNCTION_RELOAD_EDIT:
            draft.reloadEdit = action.value
            return draft

        default:
            return draft
    }
})
export default silSafetyFunctionReducer
